import { Flex } from "@chakra-ui/react";
import { useSbUsdVault, useSbUsdDeposit, useSbUsdWithdraw } from "./sbUsdHooks";
import VaultDWB from "@components/V2/Vault/Common/VaultDWB";
import Portfolio from "@components/V2/Vault/Common/Portfolio";
// import DefiPartnerships from "@components/V2/Vault/Common/DefiPartnerships";
import { DepositProps } from "@components/V2/Vault/Common/Deposit";
import { WithdrawProps } from "@components/V2/Vault/Common/Withdraw";
import { LSTIconMapping, IS_STAKING_PAUSED } from "@constants/common";
import StableClaimWrapper from "./StableClaimWrapper";
import VaultHeader from "@components/V2/Vault/Common/VaultHeader";
import Tooltip from "@components/Tooltip";
import { Typography } from "@kelp-dao/kernel-design-system";

const StableVault = () => {
  const { vaultHeaderData, portfolioConfig, isSanctioned, constants } =
    useSbUsdVault();

  const depositLogic = useSbUsdDeposit();
  const withdrawLogic = useSbUsdWithdraw();

  // Convert legacy logic to new simple props
  const depositProps: DepositProps = {
    vaultTokenSymbol: "sbUSD",
    inputTokenSymbol: (depositLogic as any)?.selectedERC20Vault || "USDC",
    inputTokenIcon:
      LSTIconMapping[(depositLogic as any)?.selectedERC20Vault || "USDC"] ||
      LSTIconMapping["USDC"],
    vaultTokenIcon: LSTIconMapping["sbUSD"] || "/assets/sbUSD.png",
    inputTokenBalance: (depositLogic as any)?.tokenBalance || 0,
    vaultTokenBalance: (depositLogic as any)?.xTokenVaultBalance || 0,
    convertedAmount: (depositLogic as any)?.convertedAmount || "0",
    exchangeRateDisplay: (depositLogic as any)?.valueInUsdc
      ? `1 sbUSD = ${(depositLogic as any).valueInUsdc} USDC`
      : undefined,
    isConnected: (depositLogic as any)?.isConnected || false,
    isApproved: (depositLogic as any)?.isApproved || false,
    isSupportedChain:
      (depositLogic as any)?.isStableVaultSupportedChain !== false,
    onDeposit: (amount?: string) =>
      (depositLogic as any)?.handleClick?.(amount),
    onApprove: (amount?: string) =>
      (depositLogic as any)?.handleClick?.(amount),
    onAmountChange: (depositLogic as any)?.handleChange,
    onTokenSelect: (depositLogic as any)?.handleTokenSelect,
    checkInputConstraints: (depositLogic as any)?.checkInput,
    vaultType: "stable",
    disclaimer: <></>,
    disabled: IS_STAKING_PAUSED,
  };

  const withdrawProps: WithdrawProps = {
    vaultTokenSymbol: "sbUSD",
    outputTokenSymbol: withdrawLogic.selectedOutputToken || "USDC",
    vaultTokenIcon: LSTIconMapping["sbUSD"] || "/assets/sbUSD.png",
    outputTokenIcon:
      LSTIconMapping[withdrawLogic.selectedOutputToken || "USDC"],
    vaultTokenBalance: withdrawLogic.tokenBalance || 0,
    outputTokenBalance: withdrawLogic.outputTokenBalance || 0,
    convertedAmount: withdrawLogic.convertedAmount || "0",
    exchangeRateDisplay: withdrawLogic.valueInUsdc
      ? `1 sbUSD = ${withdrawLogic.valueInUsdc} USDC`
      : undefined,
    isConnected: withdrawLogic.isConnected || false,
    isApproved: withdrawLogic.isApproved,
    isSupportedChain: withdrawLogic.isStableVaultSupportedChain !== false,
    onWithdraw: (amount?: string) => withdrawLogic.handleClick?.(amount),
    onApprove: (amount?: string) => withdrawLogic.handleClick?.(amount),
    onAmountChange: withdrawLogic.handleChange,
    onOutputTokenSelect: withdrawLogic.setSelectedOutputToken,
    checkInputConstraints: withdrawLogic.checkInput,
    vaultType: "stable",
    enableOutputTokenSelection: true,
    disclaimer:
      "Withdrawals require a 4-day processing period. Check the Claims tab to view pending and claimable withdrawals.",
  };

  return (
    <>
      <Flex
        direction={"column"}
        alignItems={"center"}
        gap="20px"
        // p="16px"
        maxW="1279px"
        w="100%"
        mx="auto"
        pointerEvents={isSanctioned ? "none" : "auto"}
        opacity={isSanctioned ? 0.5 : 1}
      >
        <VaultHeader
          isLoadingAPYData={vaultHeaderData.isLoadingAPYData}
          isLoadingTotalStaked={vaultHeaderData.isLoadingTotalStaked}
          totalStaked={vaultHeaderData.totalStaked}
          totalStakedEth={vaultHeaderData.totalStakedEth}
          apyValue={vaultHeaderData.apyValue}
          apyLabel={vaultHeaderData.apyLabel}
          totalStakedLabel={vaultHeaderData.totalStakedLabel}
          title={vaultHeaderData.title}
          logo={vaultHeaderData.logo}
          tooltipComponent={
            <Tooltip
              placement="top"
              label={
                <Typography
                  type="body"
                  variant="xsmall"
                  className="font-normal text-xs text-text-primary"
                >
                  7 day trailing APR <br />
                  Check <strong>&apos;Strategy Reports&apos;</strong> section
                  for details
                </Typography>
              }
            />
          }
          renderRewards={<></>}
          auditedUrl={vaultHeaderData.auditedUrl}
        />

        <VaultDWB
          availableTabs={["deposit", "withdraw", "claim"]}
          tabLabels={{
            deposit: "Deposit",
            withdraw: "Withdraw",
            claim: "Claim",
          }}
          initialTab="deposit"
          depositProps={depositProps}
          withdrawProps={withdrawProps}
          claimProps={{
            rewardTokenSymbol: "sbUSD",
            claimableAmount: 0, // This will be ignored by StableClaimWrapper
            isConnected: (depositLogic as any)?.isConnected || false,
            isSupportedChain:
              (depositLogic as any)?.isVaultSupportedChain !== false,
            isClaimable: false, // This will be ignored by StableClaimWrapper
            onClaim: () => {}, // This will be ignored by StableClaimWrapper
          }}
          customClaimComponent={StableClaimWrapper}
        />

        <Portfolio
          config={portfolioConfig}
          constants={constants}
          vaultType="stable"
          xTokenSymbol="sbUSD"
          asset="sbusd"
          aprTooltip="Realised APR post fee"
        />

        {/* <DefiPartnerships asset="sbusd" /> */}
      </Flex>
    </>
  );
};

export default StableVault;
